import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Toolbar,
  Stack,
  Tooltip,
  Divider,
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import logo from '@/assets/logo.svg';
import { MENU } from '@/routes/menu';
import usePermissions from '@/permissions/usePermissions';
import env from '@/config/env';

export const DRAWER_WIDTH = 264;
export const DRAWER_WIDTH_COLLAPSED = 76;

// Permission-filtered navigation drawer. On desktop it can collapse to an icon-only rail.
export default function Sidebar({ open, onClose, variant, collapsed = false }) {
  const { has } = usePermissions();
  // Mini mode only applies to the permanent (desktop) drawer; the temporary one is always full.
  const mini = collapsed && variant === 'permanent';
  const width = mini ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const content = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: mini ? 1 : 2.5, justifyContent: mini ? 'center' : 'flex-start' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box component="img" src={logo} alt="logo" sx={{ width: 34, height: 34 }} />
          {!mini && (
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {env.appName}
            </Typography>
          )}
        </Stack>
      </Toolbar>
      <Box sx={{ overflowX: 'hidden', overflowY: 'auto', flex: 1, px: 1.5, pb: 2 }}>
        {MENU.map((group, gi) => {
          const items = group.items.filter((i) => has(i.permission));
          if (!items.length) return null;
          return (
            <Box key={group.section} sx={{ mt: 1.5 }}>
              {mini ? (
                gi > 0 && <Divider sx={{ my: 1 }} />
              ) : (
                <Typography variant="overline" sx={{ px: 1.5, color: 'text.secondary' }}>
                  {group.section}
                </Typography>
              )}
              <List dense disablePadding>
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Tooltip key={item.path} title={mini ? item.label : ''} placement="right">
                      <ListItemButton
                        component={NavLink}
                        to={item.path}
                        onClick={variant === 'temporary' ? onClose : undefined}
                        sx={{
                          borderRadius: 1.5,
                          mb: 0.25,
                          justifyContent: mini ? 'center' : 'flex-start',
                          px: mini ? 1.5 : 2,
                          '&.active': { bgcolor: 'primary.main', color: 'primary.contrastText',
                            '& .MuiListItemIcon-root': { color: 'primary.contrastText' } },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: mini ? 0 : 38 }}>
                          <Icon fontSize="small" />
                        </ListItemIcon>
                        {!mini && <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />}
                      </ListItemButton>
                    </Tooltip>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          borderRight: '1px solid rgba(145,158,171,0.16)',
          overflowX: 'hidden',
          transition: (t) =>
            t.transitions.create('width', {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      {content}
    </Drawer>
  );
}
